import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/header/HeadingOne';
import BarcodeScanner from './BarcodeScanner';
import VisitorDetail from './VisitorDetails';
import ApiService from '../../services/ApiService';
import ModalContent from './ModalContent';
import TextButton from '../../components/buttons/TextButton';
import InputModal from './InputModal';
import Button from '../../components/buttons/Button';
import RedeemVoucher from './RedeemVoucher';
interface ErrorType {
  response: {
    data: {
      error: {
        message: string;
        booking_date: string;
        booking_id: string;
        card_hash: string;
        cardholder_pax: number;
        client_id: string | null;
        complimentary_pax: number;
        external_lounge: string | null;
        guest_pax: number;
        id: string;
        lounge: Lounge;
        lounge_id: string;
        paid_pax: number;
        status: string;
        success: boolean;
        transaction: Transaction;
        transaction_id: string;
        user_id: string | null;
        card_details: CardDetails;
        qr_already_processed: boolean;
      };
    };
  };
}
interface Lounge {
  id: string;
  lounge_id: string;
  name: string;
  location: string;
}
interface Transaction {
  id: string;
  booking_id: string;
  transaction_id: string;
  amount: number;
  last_4_digits: string;
  currency: string;
  status: string;
  transaction_date: string;
  payment_method: string;
  cardholder_name: string;
  receipt_url?: string;
  discount_applied?: boolean;
}
interface CardDetails {
  bank_name: string;
  network: string;
  bank_code: string;
  type: string;
}
interface Details {
  booking_date: string;
  booking_id: string;
  card_hash: string;
  cardholder_pax: number;
  client_id: string | null;
  complimentary_pax: number;
  external_lounge: string | null;
  guest_pax: number;
  id: string;
  lounge: Lounge;
  lounge_id: string;
  message: string;
  paid_pax: number;
  status: string;
  success: boolean;
  transaction: Transaction;
  transaction_id: string;
  user_id: string | null;
  card_details: CardDetails;
  qr_already_processed: boolean;
}
function Home() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [barcodeDetails, setBarcodeDetails] = useState<string | null>(null);
  const [visitorDetails, setVisitorDetails] = useState<Details | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isInputOpen, setIsInputOpen] = useState<boolean>(false);
  const [inputVal, setInputVal] = useState<string | null>(null);

  const closeModal = () => setIsModalOpen(false);
  const closeInputModal = () => setIsInputOpen(false);

  const handleNavigation = () => {
    navigate('/transactions');
  };

  const handleVouchersNavigation = () => {
    navigate('/voucher-report');
  };

  const openTransactionInputModal = () => {
    setIsInputOpen(true);
  };

  const verifyBarcode = useCallback(
    async (qrCode: string, isTransactionId: boolean) => {
      const data = {
        data: '',
      };
      if (!isTransactionId) {
        const cleanedBarcode = qrCode?.replace(/Shift/g, '');
        const cleanedString = cleanedBarcode.replace(/"/g, '');
        data.data = cleanedString;
      } else {
        data.data = qrCode;
      }

      try {
        let response;
        if (!isTransactionId) {
          response = await ApiService.post('v1/user/qr/validate', data);
        } else {
          response = await ApiService.post('v1/user/validate/transaction', {
            transaction_id: data.data,
          });
        }
        if (!response) {
          setIsModalOpen(true);
          setBarcodeDetails(null);
          setInputVal(null);
        } else {
          setVisitorDetails(response.data);
          setBarcodeDetails(null);
          setInputVal(null);
        }
      } catch (error: unknown) {
        const err = error as ErrorType;
        if (!err.response || !err.response?.data?.error?.qr_already_processed) {
          setErrorMessage(err?.response?.data?.error?.message);
          setIsModalOpen(true);
          setBarcodeDetails(null);
          setInputVal(null);
        } else {
          setVisitorDetails(err.response.data.error);
          setBarcodeDetails(null);
          setInputVal(null);
        }
      }
    },
    [],
  );

  useEffect(() => {
    if (barcodeDetails) {
      verifyBarcode(barcodeDetails, false);
    }
    if (inputVal) {
      verifyBarcode(inputVal, true);
    }
  }, [barcodeDetails, verifyBarcode, inputVal]);

  return (
    <div
      className="bg-lightBlue flex h-fit min-h-screen flex-col items-center overflow-x-hidden"
      data-testid="manager-home-page"
    >
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.85)]">
          <ModalContent
            closeModal={closeModal}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            setIsInputOpen={setIsInputOpen}
          />
        </div>
      )}

      {isInputOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.85)]">
          <InputModal
            closeModal={closeInputModal}
            InputChangeHandler={setInputVal}
          />
        </div>
      )}
      <div
        className={`flex w-full flex-col p-6 lg:p-10 xl:w-[1200px] 2xl:p-10 ${
          barcodeDetails ? 'gap-9' : 'gap-10 2xl:gap-[60px]'
        }`}
      >
        {/* <Logo /> */}
        <Header />
        {!visitorDetails ? (
          <div className="flex w-full gap-6">
            <div className="mx-auto flex flex-col items-center gap-6 2xl:gap-8">
              <BarcodeScanner
                setBarcodeDetails={setBarcodeDetails}
                barcodeDetails={barcodeDetails}
              />
              <div className="w-full text-center text-sm">-or-</div>
              <div className="w-full">
                <Button
                  onClick={openTransactionInputModal}
                  text="Enter Unique ID manually"
                  type="secondary"
                  data-testid="enter-uid-manually-button"
                />
              </div>

              <TextButton
                onClick={handleNavigation}
                text="View all transactions"
                data-testid="view-transactions-link"
                svg={
                  <svg
                    width="18"
                    height="19"
                    viewBox="0 0 18 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.773 9.63797L7.14798 15.263C7.09571 15.3152 7.03367 15.3567 6.96539 15.385C6.8971 15.4133 6.82392 15.4278 6.75001 15.4278C6.6761 15.4278 6.60291 15.4133 6.53463 15.385C6.46634 15.3567 6.4043 15.3152 6.35204 15.263C6.29978 15.2107 6.25832 15.1487 6.23004 15.0804C6.20175 15.0121 6.18719 14.9389 6.18719 14.865C6.18719 14.7911 6.20175 14.7179 6.23004 14.6496C6.25832 14.5813 6.29978 14.5193 6.35204 14.467L11.5798 9.24L6.35204 4.01297C6.24649 3.90742 6.18719 3.76427 6.18719 3.615C6.18719 3.46573 6.24649 3.32258 6.35204 3.21703C6.45759 3.11148 6.60074 3.05219 6.75001 3.05219C6.89927 3.05219 7.04243 3.11148 7.14798 3.21703L12.773 8.84203C12.8253 8.89427 12.8668 8.95631 12.8951 9.02459C12.9234 9.09288 12.9379 9.16608 12.9379 9.24C12.9379 9.31392 12.9234 9.38712 12.8951 9.4554C12.8668 9.52369 12.8253 9.58573 12.773 9.63797Z"
                      fill="#1C64F2"
                    />
                  </svg>
                }
              />
            </div>
            <div className="mx-auto flex flex-col items-center gap-6 2xl:gap-8">
              <RedeemVoucher />
              <TextButton
                onClick={handleVouchersNavigation}
                text="View all Redeemed Voucher"
                data-testid="view-vouchers-link"
                svg={
                  <svg
                    width="18"
                    height="19"
                    viewBox="0 0 18 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.773 9.63797L7.14798 15.263C7.09571 15.3152 7.03367 15.3567 6.96539 15.385C6.8971 15.4133 6.82392 15.4278 6.75001 15.4278C6.6761 15.4278 6.60291 15.4133 6.53463 15.385C6.46634 15.3567 6.4043 15.3152 6.35204 15.263C6.29978 15.2107 6.25832 15.1487 6.23004 15.0804C6.20175 15.0121 6.18719 14.9389 6.18719 14.865C6.18719 14.7911 6.20175 14.7179 6.23004 14.6496C6.25832 14.5813 6.29978 14.5193 6.35204 14.467L11.5798 9.24L6.35204 4.01297C6.24649 3.90742 6.18719 3.76427 6.18719 3.615C6.18719 3.46573 6.24649 3.32258 6.35204 3.21703C6.45759 3.11148 6.60074 3.05219 6.75001 3.05219C6.89927 3.05219 7.04243 3.11148 7.14798 3.21703L12.773 8.84203C12.8253 8.89427 12.8668 8.95631 12.8951 9.02459C12.9234 9.09288 12.9379 9.16608 12.9379 9.24C12.9379 9.31392 12.9234 9.38712 12.8951 9.4554C12.8668 9.52369 12.8253 9.58573 12.773 9.63797Z"
                      fill="#1C64F2"
                    />
                  </svg>
                }
              />
            </div>
          </div>
        ) : (
          <VisitorDetail
            details={visitorDetails}
            setVisitorDetails={setVisitorDetails}
          />
        )}
      </div>
    </div>
  );
}
export default Home;
